import { ref } from 'vue';

export const sleep = (ms: number) => {
  return new Promise((r) => {
    setTimeout(r, ms);
  });
};

export function uuid(): string;
export function uuid(value: string): boolean;
export function uuid(value?: string) {
  if (value !== undefined) {
    return /^[a-f\d]{8}(-[a-f\d]{4}){3}-[a-f\d]{12}$/i.test(value);
  }

  if (self && self.crypto && typeof self.crypto.randomUUID === 'function') {
    return self.crypto.randomUUID();
  }

  const chars = 'abcdef0123456789';

  const randomChars = (len: number) => {
    let r = '';
    for (let i = 0; i < len; i++) {
      r += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return r;
  };

  // 86d45003-dd41-4ad8-8c08-2e9acf2abf0c
  const uuidString =
    randomChars(8) +
    '-' +
    randomChars(4) +
    '-' +
    randomChars(4) +
    '-' +
    randomChars(4) +
    '-' +
    randomChars(12);
  return uuidString;
}

export const mmss = (ms: number) => {
  let minutes = Math.floor(ms / 60000);
  let seconds = Math.round((ms % 60000) / 1000);
  if (seconds === 60) {
    minutes++;
    seconds = 0;
  }
  return minutes.toString().padStart(2, '0') + ':' + seconds.toString().padStart(2, '0');
};

export const formatDate = (d: string | null | undefined) => {
  if (!d || d === '' || d === '0001-01-01T00:00:00Z') {
    return '-';
  }
  return new Date(d).toDateString();
};

export const formatDay = (s: string) =>
  new Intl.DateTimeFormat('default', {
    month: 'short',
    day: 'numeric',
  }).format(new Date(s));

export const formatTime = (s: string | null | undefined) => {
  if (!s || s === '' || s === '0001-01-01T00:00:00Z') {
    return '-';
  }

  const d = new Date(s);
  return d.getHours() + ':' + (d.getMinutes() < 10 ? '0' : '') + d.getMinutes();
};

export const formatDateTime = (d: string | null | undefined) => {
  const date = formatDate(d);
  const time = formatTime(d);
  if (date === '-' && time === '-') {
    return '-';
  }
  return `${date} @ ${time}`;
};

export const formatHumanSocratStatus = (s: string): string => {
  switch (s) {
    case 'REJECTED':
      return 'Rejected';
    case 'NOT_STARTED':
      return 'Ready for analysis';
    case 'LOW_TRANSCRIPTION_QUALITY':
      return 'Low transcription quality';
    case 'SUBMITTED':
      return 'Submitted';
    case 'IN_PROGRESS':
      return 'In progress';
    case 'COMPLETED':
      return 'Completed';
    case '':
      return 'Processing';
  }
  return 'Unknown';
};

/**
 * Get current UTC timestamp.
 * @returns number
 */
export const timestamp_utc = () => {
  return Math.floor(Date.now() / 1000);
};

export const useTextMeasure = () => {
  const hiddenContainer = ref<HTMLElement | null>(null);
  const el = document.createElement('div');
  el.style.position = 'absolute';
  el.style.width = '100vw';
  el.style.height = '100vh';
  el.style.top = '-100vw';
  el.style.left = '-100vh';
  el.style.whiteSpace = 'nowrap';
  const debug = false;
  if (debug) {
    el.style.zIndex = '100';
    el.style.pointerEvents = 'none';
    el.style.top = '10px';
    el.style.left = '10px';
  } else {
    el.style.visibility = 'hidden';
  }
  document.body.appendChild(el);
  hiddenContainer.value = el;

  const getCssText = (element: HTMLElement) => {
    if ('computedStyleMap' in element) {
      const styles = Array.from(element.computedStyleMap()) as [
        string,
        CSSStyleValue,
      ][];
      const rules: string[] = [];

      for (const [property, valueObj] of styles) {
        let value = valueObj.toString();
        if (property === 'display') {
          value = 'inline';
        }
        rules.push(`${property}:${value}`);
      }
      return rules.join(';\n');
    }

    // firefox
    const styles = window.getComputedStyle(element);
    const rules: string[] = [];

    for (const property of Array.from(styles)) {
      let value = styles.getPropertyValue(property);
      if (property === 'width' || property === 'height') {
        value = 'auto';
      }
      if (property === 'display') {
        value = 'inline';
      }
      rules.push(`${property}:${value}`);
    }
    return rules.join(';\n');
  };
  const getSize = (element: HTMLElement, text: string): number => {
    let el = element;
    if (!element && hiddenContainer.value) {
      el = hiddenContainer.value;
    }

    el.textContent = text;
    return el.offsetWidth;
  };
  return {
    getSize,
    getMaxSize: (element: HTMLElement, text: string[]): number => {
      const el = document.createElement('span');

      el.style = getCssText(element);
      hiddenContainer.value?.appendChild(el);

      let maxWidth = 0;
      // Measure each title's width
      for (const str of text) {
        const width = getSize(el, str);
        if (width > maxWidth) {
          maxWidth = width;
        }
      }

      hiddenContainer.value?.removeChild(el);

      return maxWidth;
    },
  };
};

/**
 * replaces an element in the list determined by key, in place.
 * @param collection
 * @param element
 * @param key
 * @return any[] the modified collection
 */
export const replaceBy = (
  collection: { [key: string]: any }[],
  element: { [key: string]: any },
  key = 'id',
) => {
  const idx = collection.findIndex((i) => i[key] === element[key]);
  collection.splice(idx, 1, element);
  return [...collection];
};

export const parseBackendException = (message: string) => {
  const matches = /^code=(\d+),\s+message=(.*)$/i.exec(message);

  if (!matches || matches.length < 2) {
    return {
      code: 0,
      message,
    };
  }
  return {
    code: parseInt(matches[1] ?? '0', 10),
    message: matches[2],
  };
};

export const formatShortDuration = (
  timestamp?: string,
  maxDays: number = 7,
) => {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  const difference = Math.round((new Date().getTime() - date.getTime()) / 1000);
  const days = Math.floor(difference / 86400);

  if (days > 0) {
    if (days >= maxDays) {
      return `${maxDays}+ days`;
    }
    return `${days} days`;
  }

  const hours = Math.floor((difference % 86400) / 3600);
  const minutes = Math.floor((difference % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

export const startBlobDownload = (MIME: string, data: any, filename: string) => {
  const myBlob = new Blob([data], { type: MIME });
  const blobURL = URL.createObjectURL(myBlob);

  const a = document.createElement('a');
  a.setAttribute('href', blobURL);
  a.setAttribute('download', filename);

  a.style.display = 'none';
  document.body.appendChild(a);

  a.click();

  document.body.removeChild(a);
  URL.revokeObjectURL(blobURL);
};
